import type { GreenCoffee, GreenCoffeeFieldKey, GreenCoffeeFields } from '@/hooks/useGreenCoffee';

// Short names for the judge rubric's FAIL rules, in the order the rubric
// numbers them (apps/api-cf/scripts/judge-eval/judge.ts, RUBRIC).
const RULE_NAMES: Record<number, string> = {
  1: 'missing value',
  2: 'invented value',
  3: 'wrong value',
  4: 'variety dropped or renamed',
  5: 'not in English',
  6: 'process lost a flavor',
  7: 'compound note broken',
  8: 'note is not a flavor',
  9: 'blend flag wrong',
};

export function ruleName(rule: number): string | null {
  return RULE_NAMES[rule] ?? null;
}

// Published with no human grade: only the judge publishes that way.
export function isJudgePublished(green: GreenCoffee): boolean {
  return green.publishedCoffeeId !== null && green.grade === null;
}

// A row a human can no longer grade: already graded, already live, or holding
// a judge PASS whose publish is still in flight. The server answers 400 to a
// grade on any of these, so the card offers no Good or Bad.
export function isGradeLocked(green: GreenCoffee): boolean {
  return (
    green.grade !== null || green.publishedCoffeeId !== null || green.judge?.verdict === 'PASS'
  );
}

// A human Good that carries the admin's own field edits.
export function isCorrected(green: GreenCoffee): boolean {
  return green.grade === 'good' && (green.correctedFields?.length ?? 0) > 0;
}

// In the order the card lists the fields.
export const FIELD_LABELS: Record<GreenCoffeeFieldKey, string> = {
  name: 'Name',
  countryCode: 'Country',
  region: 'Region',
  farm: 'Farm',
  processingType: 'Process',
  varieties: 'Varieties',
  tastingNotes: 'Tasting notes',
  isBlend: 'Blend',
};

export function fieldLabels(keys: readonly GreenCoffeeFieldKey[]): string {
  return (Object.keys(FIELD_LABELS) as GreenCoffeeFieldKey[])
    .filter((key) => keys.includes(key))
    .map((key) => FIELD_LABELS[key])
    .join(', ');
}

export function correctedNote(green: GreenCoffee): string | null {
  return isCorrected(green) ? `Corrected: ${fieldLabels(green.correctedFields ?? [])}` : null;
}

// Mirrors the server's diff: arrays compare in order.
export function changedFieldKeys(
  before: GreenCoffeeFields,
  after: GreenCoffeeFields,
): GreenCoffeeFieldKey[] {
  return (Object.keys(FIELD_LABELS) as GreenCoffeeFieldKey[]).filter(
    (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
  );
}

function cleanText(value: string | null): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

// What the correct endpoint accepts: trimmed, with an empty input sent as null.
export function normalizeFields(fields: GreenCoffeeFields): GreenCoffeeFields {
  return {
    name: fields.name.trim(),
    isBlend: fields.isBlend,
    farm: cleanText(fields.farm),
    region: cleanText(fields.region),
    countryCode: fields.countryCode,
    processingType: cleanText(fields.processingType),
    varieties: fields.varieties,
    tastingNotes: fields.tastingNotes,
  };
}
